import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { FOUNDER } from "@/lib/siteContent";
import { useContent } from "@/lib/content";

const JOEL = "/brand/joel-kotho.jpg";
const MEDITERRANEAN =
    "https://images.pexels.com/photos/30608241/pexels-photo-30608241.jpeg?auto=compress&cs=tinysrgb&w=2400"; // Monaco marina with yachts

const values = [
    {
        kicker: "I",
        title: "Discrétion",
        desc: "Les informations sont partagées avec les seuls intervenants nécessaires à la mission, dans un cadre de confidentialité convenu avec le client.",
    },
    {
        kicker: "II",
        title: "Rigueur",
        desc: "Une méthodologie issue de la finance internationale et du conseil aux dirigeants. Anticiper, structurer, sécuriser.",
    },
    {
        kicker: "III",
        title: "Sélectivité",
        desc: "Un nombre restreint de mandats par trimestre. L'attention totale, sans dilution, à chaque opération acceptée.",
    },
    {
        kicker: "IV",
        title: "Réseau",
        desc: "Un cercle international de family offices, investisseurs privés et experts indépendants, bâti sur deux décennies.",
    },
];

export default function About() {
    const content = useContent();
    return (
        <div data-testid="about-page">
            {/* HERO */}
            <section className="relative pt-32 md:pt-44 pb-24 md:pb-32 bg-xv-ivory overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                À propos
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-black text-[48px] sm:text-6xl md:text-7xl lg:text-[100px] leading-[0.98] tracking-tight max-w-5xl"
                            style={{ fontWeight: 300 }}
                        >
                            Un cabinet fondé sur{" "}
                            <em className="not-italic text-xv-midnight">
                                la finance privée
                            </em>{" "}
                            et l'accompagnement stratégique.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-14 max-w-2xl text-xv-text-secondary text-[17px] md:text-[19px] leading-[1.8] font-light">
                            XV Capital Partners est un cabinet
                            indépendant de strategic advisory, dédié
                            aux opérations stratégiques et financières
                            des dirigeants, investisseurs privés et
                            family offices. Discret par choix, exigeant
                            par méthode.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* FOUNDER — Joel Kotho only */}
            <section
                data-testid="founder"
                className="bg-xv-cream py-28 md:py-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-16 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                Fondateur
                            </span>
                        </div>
                    </Reveal>

                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="relative overflow-hidden bg-xv-black">
                                    <img
                                        src={JOEL}
                                        alt="Joel Kotho — Founder & Managing Partner"
                                        className="w-full h-[560px] md:h-[720px] object-cover object-top"
                                    />
                                </div>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-7 lg:pl-8">
                            <Reveal delay={0.1}>
                                <div className="overline text-xv-gold mb-6">
                                    Founder & Managing Partner
                                </div>
                                <h2
                                    className="font-serif text-xv-black text-[48px] md:text-6xl lg:text-[80px] leading-[1.0] mb-10"
                                    style={{ fontWeight: 300 }}
                                >
                                    Joel Kotho
                                </h2>
                                <div className="space-y-6 text-xv-text-secondary text-[16px] md:text-[17px] leading-[1.9] font-light max-w-xl">
                                    <p className="text-xv-black font-serif text-[19px] md:text-[22px] leading-snug">
                                        {content.about_founder?.intro || FOUNDER.domain}
                                    </p>
                                    <p>
                                        Formé aux métiers de la{" "}
                                        <span className="text-xv-black">
                                            comptabilité et de la gestion
                                        </span>
                                        , son parcours l'a conduit à
                                        évoluer dans les univers de la{" "}
                                        <span className="text-xv-black">
                                            banque, de l'assurance, de
                                            l'industrie et de
                                            l'entrepreneuriat
                                        </span>{" "}
                                        — lui offrant une compréhension
                                        concrète des enjeux financiers,
                                        patrimoniaux et stratégiques
                                        auxquels sont confrontés les
                                        décideurs.
                                    </p>
                                    <p>
                                        Au fil des années, il a développé
                                        un{" "}
                                        <span className="text-xv-black">
                                            réseau de confiance
                                        </span>{" "}
                                        réunissant dirigeants,
                                        investisseurs, family offices,
                                        partenaires stratégiques et
                                        acteurs institutionnels entre
                                        l'Europe, l'Afrique, le
                                        Moyen-Orient et l'Amérique du
                                        Nord. Cette capacité à connecter
                                        les bonnes expertises et les bons
                                        interlocuteurs autour de
                                        situations complexes constitue
                                        aujourd'hui l'un des fondements
                                        de l'approche XV Capital
                                        Partners.
                                    </p>
                                    {FOUNDER.institutions?.length > 0 && (
                                        <div className="border-t border-b border-xv-gold/30 py-6 my-2">
                                            <div className="overline text-xv-gold mb-4">
                                                Parcours institutionnel
                                            </div>
                                            <div className="flex flex-wrap gap-x-8 gap-y-3">
                                                {FOUNDER.institutions.map((inst) => (
                                                    <span
                                                        key={inst}
                                                        className="font-serif text-[19px] md:text-[22px] text-xv-black"
                                                        style={{ fontWeight: 400 }}
                                                    >
                                                        {inst}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <p>
                                        Parallèlement à ce parcours
                                        professionnel, il représente le{" "}
                                        <span className="text-xv-black">
                                            Congo au niveau international
                                            en rugby
                                        </span>
                                        . Cette expérience du haut niveau
                                        lui transmet une culture durable
                                        de la discipline, de
                                        l'engagement, de la préparation
                                        et de la responsabilité
                                        collective, qui continue
                                        aujourd'hui d'inspirer son
                                        approche des projets complexes et
                                        des environnements exigeants.
                                    </p>
                                    <p>
                                        Sa conviction est simple : les
                                        opérations les plus sensibles
                                        exigent davantage qu'une
                                        expertise technique. Elles
                                        nécessitent une capacité à{" "}
                                        <span className="text-xv-black">
                                            coordonner des intérêts
                                            multiples
                                        </span>
                                        , à créer les bonnes conditions
                                        de confiance et à inscrire chaque
                                        décision dans une vision de long
                                        terme.
                                    </p>
                                    <p>
                                        À travers XV Capital Partners,
                                        Joel Kotho accompagne des
                                        opérations de croissance, de
                                        transmission, de structuration
                                        patrimoniale et de développement
                                        international selon une même
                                        exigence :{" "}
                                        <span className="text-xv-black">
                                            engagement, discrétion,
                                            sélectivité et vision long
                                            terme
                                        </span>
                                        .
                                    </p>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* PERSONAL PILLARS — sport / engagement / discipline / résilience */}
            <section
                data-testid="founder-pillars"
                className="bg-xv-ivory py-28 md:py-36 border-t border-xv-line"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                    <span className="divider-gold" />
                                    <span className="overline">
                                        Une signature personnelle
                                    </span>
                                </div>
                                <h2
                                    className="font-serif text-xv-black text-[36px] md:text-5xl lg:text-[56px] leading-[1.04]"
                                    style={{ fontWeight: 300 }}
                                >
                                    Quatre principes,{" "}
                                    <em className="not-italic text-xv-midnight">
                                        une seule manière.
                                    </em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 self-end">
                            <Reveal delay={0.1}>
                                <p className="text-xv-text-secondary text-[16px] leading-[1.85] font-light">
                                    Les codes appris dans le sport de
                                    haut niveau structurent aujourd'hui
                                    la manière dont le cabinet conduit
                                    chaque mandat.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-xv-line">
                        {FOUNDER.pillars.map((p, i) => (
                            <Reveal key={p.t} delay={i * 0.08}>
                                <div
                                    data-testid={`founder-pillar-${i}`}
                                    className="bg-xv-ivory p-8 md:p-10 h-full hover:bg-xv-cream transition-colors duration-700"
                                >
                                    <div
                                        className="font-serif text-[42px] md:text-[52px] text-xv-gold/30 mb-2 leading-none"
                                        style={{ fontWeight: 300 }}
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <h3
                                        className="font-serif text-[24px] md:text-[28px] text-xv-black mb-5 leading-tight"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {p.t}
                                    </h3>
                                    <p className="text-xv-text-secondary text-[15px] leading-[1.85] font-light">
                                        {p.d}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* VALUES */}
            <section
                data-testid="values"
                className="bg-xv-ivory py-28 md:py-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-12 mb-20">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                    <span className="divider-gold" />
                                    <span className="overline">
                                        Quatre principes
                                    </span>
                                </div>
                                <h2
                                    className="font-serif text-xv-black text-[40px] md:text-5xl lg:text-[60px] leading-[1.04]"
                                    style={{ fontWeight: 300 }}
                                >
                                    Ce qui guide{" "}
                                    <em className="not-italic text-xv-midnight">
                                        chaque mandat.
                                    </em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 self-end">
                            <Reveal delay={0.1}>
                                <p className="text-xv-text-secondary text-[16px] md:text-[17px] leading-[1.85] font-light">
                                    La confiance ne se demande pas. Elle
                                    se gagne, dossier par dossier, par
                                    un engagement sans compromis et une
                                    discrétion absolue.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-px bg-xv-line">
                        {values.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.08}>
                                <div
                                    data-testid={`value-${i}`}
                                    className="p-10 md:p-14 bg-xv-ivory hover:bg-xv-cream transition-colors duration-700"
                                >
                                    <div className="flex items-start gap-6 mb-8">
                                        <span
                                            className="font-serif text-3xl text-xv-gold"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {v.kicker}
                                        </span>
                                        <span className="block h-px w-12 bg-xv-gold mt-5" />
                                    </div>
                                    <h3
                                        className="font-serif text-3xl md:text-4xl text-xv-black mb-6"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {v.title}
                                    </h3>
                                    <p className="text-xv-text-secondary text-[15px] md:text-[16px] leading-[1.85] font-light max-w-md">
                                        {v.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TEASE — ecosystem of experts */}
            <section
                data-testid="ecosystem-tease"
                className="relative bg-xv-midnightDeep text-xv-ivory py-28 md:py-40 overflow-hidden"
            >
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                    <img
                        src={MEDITERRANEAN}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-xv-midnightDeep/80" />
                </div>
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                Un cercle d'experts
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="font-serif text-[40px] md:text-5xl lg:text-[64px] leading-[1.04] max-w-3xl mb-12"
                            style={{ fontWeight: 300 }}
                        >
                            Au-delà d'une équipe,{" "}
                            <em className="not-italic text-xv-goldSoft">
                                un écosystème.
                            </em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="text-xv-ivory/80 text-[17px] leading-[1.85] font-light max-w-2xl mb-12">
                            XV Capital Partners coordonne un cercle
                            restreint d'experts indépendants — droit
                            des sociétés, fiscalité, audit, due
                            diligence, gouvernance, relations
                            investisseurs — mobilisés sur-mesure pour
                            chaque opération.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <Link
                            to="/approche"
                            data-testid="ecosystem-cta"
                            className="group inline-flex items-center gap-4 px-8 py-4 border border-xv-ivory/40 text-xv-ivory text-[11px] tracking-[0.3em] uppercase hover:bg-xv-ivory hover:text-xv-midnightDeep transition-all duration-700"
                        >
                            Découvrir l'approche
                            <ArrowUpRight
                                strokeWidth={1.2}
                                size={16}
                                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                            />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-xv-ivory py-28 md:py-32">
                <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
                    <Reveal>
                        <h2
                            className="font-serif text-xv-black text-[36px] md:text-5xl lg:text-[60px] leading-[1.05]"
                            style={{ fontWeight: 300 }}
                        >
                            Une opération à structurer{" "}
                            <em className="not-italic text-xv-midnight">
                                avec exigence ?
                            </em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact"
                            data-testid="about-cta"
                            className="group mt-14 inline-flex items-center gap-4 px-10 py-5 bg-xv-midnight text-xv-ivory text-[11px] tracking-[0.3em] uppercase hover:bg-xv-black transition-all duration-700"
                        >
                            Premier entretien confidentiel
                            <ArrowUpRight
                                strokeWidth={1.2}
                                size={16}
                                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                            />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
